import React from 'react'
import { connect } from 'react-redux'
import { userTokenSelector } from 'Store/customer/selector'
import { RootState } from 'Store'
import { Col, Empty, Row, Space } from 'antd'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { selectCurrentUser } from 'Store/user/selector'
import { getRmxIngredientType, numberWithComma, percentCalulator } from 'helpers/format'
import { Pie } from 'Components/utilities/Chart'
import { MaterialData } from 'Store/minirmx-dashboard/types'

type Props = {
  datas: any[]
} & ReduxProps

const IngredientReport: React.FunctionComponent<Props> = props => {
  const { datas } = props

  const getValue = (key: any) => {
    if (datas && datas.length > 0) {
      return datas[0][key]
    }
    return 0
  }

  const dataList = [
    {
      title: 'ปูนซีเมนต์',
      value: getValue('cement'),
    },
    {
      title: 'ทราย',
      value: getValue('sand'),
    },
    {
      title: 'หิน',
      value: getValue('rock'),
    },
  ] as any[]
  const getResult = () => {
    const label = [] as string[]
    const color = [] as string[]
    const value = [] as any[]
    dataList.forEach(item => {
      label.push(item.title)
      color.push(getRmxIngredientType(item.title).color)
      value.push(item.value || 0)
    })
    return { label: label, color: color, value: value }
  }
  const result = getResult()

  const options = {
    responsive: true,
    legend: {
      display: false,
      position: 'right',
      align: 'middle',
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem: any, data: any) => {
          const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || 0
          const label = data.datasets[tooltipItem.datasetIndex].label[tooltipItem.index] || ''
          return `${label} ${numberWithComma(value, true)} กิโลกรัม`
        },
      },
    },
  }

  const data = {
    labels: result.label,
    datasets: [
      {
        label: result.label,
        data: result.value,
        backgroundColor: result.color,
        borderWidth: 1,
      },
    ],
  }

  const isEmpty = (result: number[]) => {
    let empty = true as boolean
    result.forEach((item: number) => {
      if (item > 0) {
        empty = false
        return
      }
    })
    return empty
  }

  const sumary = () => {
    let sum = 0
    dataList.forEach(item => {
      sum = sum + item.value
    })
    return sum
  }

  const calculatePercent = (val: number) => {
    const total = sumary()
    return percentCalulator(val, total)
  }

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <BoxTitle>รายงานการใช้ส่วนผสมวัตถุดิบ</BoxTitle>
      </Col>
      <Col md={24} xs={24}>
        <BoxWrapper>
          <Row justify="start" align="bottom">
            <Col span={24}>
              {!isEmpty(result.value) ? (
                <Row align="middle" gutter={[8, 8]}>
                  <Col md={12} xs={24}>
                    <Pie data={data} options={options} />
                  </Col>
                  <Col md={12} xs={24}>
                    <Row gutter={[8, 8]}>
                      <Col span={24}>
                        <Space>
                          <Legend>
                            <PencentLegend>น้ำ &nbsp;</PencentLegend>:{' '}
                            {numberWithComma(getValue('water'), true)} ลิตร
                          </Legend>
                        </Space>
                        <br />
                      </Col>
                      {dataList.map((item, index) => {
                        return (
                          <Col span={24} key={index}>
                            <Space align="baseline">
                              <Point style={{ backgroundColor: result.color[index] }} />
                              <PencentLegend style={{ color: result.color[index] }}>
                                {numberWithComma(calculatePercent(result.value[index]), true)}%
                              </PencentLegend>
                              <Legend>
                                <PencentLegend style={{ color: result.color[index] }}>
                                  {result.label[index]} &nbsp;
                                </PencentLegend>
                                : {numberWithComma(result.value[index], true)} กก.
                              </Legend>
                            </Space>
                            <br />
                          </Col>
                        )
                      })}
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Legend>
                      หมายเหตุ: ข้อมูลปริมาณ ปูนซีเมนต์, หิน, ทราย และ น้ำ จากรายงานที่ออกโดยระบบ Mini RMX
                      iTrack ใช้สำหรับการคำนวน Mix Design เท่านั้น
                      บริษัทไม่รับรองและไม่มีความรับผิดใดต่อความถูกต้องและสมบูรณ์ของข้อมูล
                      หรือการนำข้อมูลดังกล่าวไปใช้เพื่อวัตถุประสงค์อย่างอื่น
                    </Legend>
                  </Col>
                </Row>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Col>
          </Row>
        </BoxWrapper>
      </Col>
    </Row>
  )
}

const BoxWrapper = styled.div`
  padding: 20px 10px;
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};
  border-radius: 10px;
  border-color: transparent;
`

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body2};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

const Point = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
`
const Legend = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const PencentLegend = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.drakGrey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
`

const mapStateToProps = (state: RootState) => {
  return {
    token: userTokenSelector(state),
    currentUser: selectCurrentUser(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(IngredientReport)
